import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { translations } from '../i18n/translations';
import { navigate, PageProps } from 'gatsby';

type Language = 'en' | 'uk' | 'ru';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => any;
  getApiDocsUrl: () => string;
  getUrl: (path: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const locales = {
  en: { lang: "English", path: "/en" },
  ru: { lang: "Русский", path: "/ru" },
  uk: { lang: "Українська", path: "/uk" },
};


export const LanguageProvider: React.FC<{ children: React.ReactNode } & Omit<PageProps, 'children'>> = ({ children, location }) => {
  const defaultLang = "ru";
  const getInitialLang = () => {
    const pathname = location.pathname.split("/")[1];
    return locales[pathname as keyof typeof locales] ? pathname : defaultLang;
  };

  const language = useMemo(() => {
    return getInitialLang() as keyof typeof locales;
  }, [location?.pathname]);

  const setLanguage = useCallback((langPayload: keyof typeof locales) => {
    const pathname = location.pathname.split(language)[1]
    navigate(locales[langPayload].path + (pathname ?? ""));
  }, [navigate, language]);

  const t = (path: string) => {
    return path.split('.').reduce((obj, key) => obj[key], translations[language] as any);
  };

  const getApiDocsUrl = () => {
    switch (language) {
      case 'en':
        return 'https://feennexen.docs.apiary.io/#';
      case 'ru':
      case 'uk':
        return 'https://feennexdocsapiaryio.docs.apiary.io/#introduction/a';
      default:
        return 'https://feennexdocsapiaryio.docs.apiary.io/#introduction/a';
    }
  };

  const getUrl = (path: string) => locales[language].path + path;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, getApiDocsUrl, getUrl }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
